// @flow

import React, { useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Recaptcha from 'react-recaptcha';

import ThemeProviderV2 from '../ThemeProviderV2';
import TopNav from '../primitives/TopNav';
import Container from '../primitives/Container';
import PrimaryButton from '../primitives/PrimaryButton';
import SecondaryButton from '../primitives/SecondaryButton';
import Card from '../primitives/Card';

import StyledPhoneInput from '../../SharedComponents/StyledPhoneInput';
import AgreeToTOCLine from '../../SharedComponents/AgreeToTOCLine';
import { useUserAuth } from '../../Auth/UserSession';
import useSignupSubmission from '../../../hooks/useSignupSubmission';
import { log, REGEX_PATTERNS } from '../../../utils/jsUtils';
import SPBackend from '../../../services/SPBackend';
import adTracking from '../../../services/adTracking';

const handleCaptchaLoaded = () => {
  log('[V2 SignupPage] captcha loaded');
};

const SignupPage = () => {
  const { register, handleSubmit, errors, control, trigger } = useForm({
    mode: 'onBlur'
  });
  const { setEmail, setPassword } = useUserAuth();

  const {
    couponValidation,
    couponCode,
    isSignupEnabled,
    canShowForm,
    trackFieldFocus,
    submit
  } = useSignupSubmission();

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Step 1 fires the Lead/SignupStep1 conversions once per page session even
  // if the user navigates Back → Continue. Ref over state to avoid re-render.
  const step1FiredRef = useRef(false);

  const handleCaptcha = (captchaResponse) => {
    if (!captchaResponse) return;
    SPBackend.verifyCaptcha({ response: captchaResponse })
      .then((response) => {
        if (response.data.success) {
          setIsVerified(true);
        } else {
          log('[V2 SignupPage] verifyCaptcha not success:', response);
        }
      })
      .catch((err) => {
        log('[V2 SignupPage] verifyCaptcha error:', err);
      });
  };

  const handleContinue = async() => {
    const ok = await trigger(['fullName', 'email', 'phone']);
    if (ok) {
      setSubmitError('');
      if (!step1FiredRef.current) {
        step1FiredRef.current = true;
        adTracking.trackSignupStep(1);
      }
      setStep(2);
    }
  };

  // Route form submission (button click or Enter key) based on the active step.
  const onFormSubmit = (e) => {
    if (step === 1) {
      e.preventDefault();
      handleContinue();
      return;
    }
    handleSubmit(createAccount)(e);
  };

  const handleBack = () => {
    setSubmitError('');
    setStep(1);
  };

  const createAccount = async(registration) => {
    if (!agreedToTerms) {
      setSubmitError('You must agree to the Terms & Conditions and Privacy Policy to create an account.');
      return;
    }
    if (!isVerified) {
      setSubmitError('Please complete the captcha verification.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      await submit({
        fullName: registration.fullName,
        email: registration.email,
        password: registration.password,
        phone: registration.phone,
        companyName: registration.companyName,
        agreed: agreedToTerms
      });
      // On success the hook navigates to /verifyPhone — this component
      // unmounts, so we deliberately do NOT touch state below this line.
    } catch (e) {
      setSubmitting(false);
    }
  };

  // Coupon state handling — the same gates used by /register.
  const renderCouponGate = () => {
    if (couponValidation.loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
            Validating your signup link…
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Hang tight while we check this in.
          </Typography>
        </Box>
      );
    }
    if (couponCode && couponValidation.valid === false) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 1.5, color: 'error.main' }}>
            Invalid signup link
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            Please contact us at{' '}
            <Link href="mailto:hi@askneo.io">hi@askneo.io</Link>{' '}for help.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">Sign in here</Link>
          </Typography>
        </Box>
      );
    }
    if (!canShowForm) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 1.5 }}>
            New signups temporarily paused
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            We're currently accepting new members by invitation only. Get in touch at{' '}
            <Link href="mailto:hi@askneo.io">hi@askneo.io</Link>.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">Sign in here</Link>
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const formAvailable = (isSignupEnabled || couponValidation.valid === true) && !couponValidation.loading
    && !(couponCode && couponValidation.valid === false);

  return (
    <ThemeProviderV2>
      <Helmet>
        <title>AskNeo | Start your free trial</title>
      </Helmet>

      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', position: 'relative' }}>
        <TopNav variant="light" showCta={false} />

        <Box sx={{ pt: { xs: 12, md: 14 }, pb: { xs: 6, md: 10 } }}>
          <Container maxWidth={1100}>
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
              {/* Form column */}
              <Grid item xs={12} md={7}>
                <Card variant="elevated" sx={{ p: { xs: 3, sm: 5, md: 6 } }}>
                  {renderCouponGate()}

                  {formAvailable && (
                    <form onSubmit={onFormSubmit} noValidate>
                      {/* Step 1 stays mounted (CSS-hidden in step 2) so react-hook-form retains values across step transitions. */}
                      <Box sx={{ display: step === 1 ? 'block' : 'none' }}>
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{ color: 'primary.main', fontWeight: 700 }}
                          >
                            Step 1 of 2
                          </Typography>
                          <Typography variant="h2" component="h1" sx={{ mt: 1, mb: 1.5, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                            Where can we send your free trial?
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 4 }}>
                            7-day free trial. No credit card required.
                          </Typography>

                          <TextField
                            fullWidth
                            label="Full Name"
                            name="fullName"
                            type="text"
                            autoComplete="name"
                            inputRef={register({
                              required: 'Full name is required.',
                              validate: (v) => ((v || '').trim().length > 0) || 'Full name is required.'
                            })}
                            onFocus={() => trackFieldFocus('fullName')}
                            error={Boolean(errors.fullName)}
                            helperText={errors.fullName?.message}
                            sx={{ mb: 2.5 }}
                          />

                          <TextField
                            fullWidth
                            label="Work Email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            onChange={(event) => setEmail(event.target.value)}
                            onFocus={() => trackFieldFocus('email')}
                            inputRef={register({
                              required: 'Email is required.',
                              pattern: { value: REGEX_PATTERNS.EMAIL, message: 'Please enter a valid email.' }
                            })}
                            error={Boolean(errors.email)}
                            helperText={errors.email?.message}
                            sx={{ mb: 2.5 }}
                          />

                          <Box sx={{ mb: 0.5 }}>
                            <Controller
                              control={control}
                              name="phone"
                              defaultValue=""
                              rules={{ required: 'Mobile number is required.' }}
                              render={({ onChange, value }) => (
                                <StyledPhoneInput
                                  variant="outlined"
                                  defaultCountry="US"
                                  autoComplete="tel"
                                  onChange={onChange}
                                  onFocus={() => trackFieldFocus('phone')}
                                  placeholder="Mobile phone number"
                                  value={value}
                                />
                              )}
                            />
                            {errors.phone && (
                              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                                {errors.phone.message}
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ mt: 4 }}>
                            <PrimaryButton type="submit" fullWidth>
                              Continue
                            </PrimaryButton>
                          </Box>

                          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 3 }}>
                            Already have an account?{' '}
                            <Link component={RouterLink} to="/login">Sign in here</Link>
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: step === 2 ? 'block' : 'none' }}>
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{ color: 'primary.main', fontWeight: 700 }}
                          >
                            Step 2 of 2
                          </Typography>
                          <Typography variant="h2" component="h1" sx={{ mt: 1, mb: 1.5, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                            Almost there — secure your account
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 4 }}>
                            One last step before your free trial begins.
                          </Typography>

                          {submitError && (
                            <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
                              {submitError}
                            </Typography>
                          )}

                          <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            onChange={(event) => setPassword(event.target.value)}
                            onFocus={() => trackFieldFocus('password')}
                            inputRef={register({
                              required: 'Password is required.',
                              minLength: { value: 6, message: 'Password must be at least 6 characters.' }
                            })}
                            error={Boolean(errors.password)}
                            helperText={errors.password?.message}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword((v) => !v)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                  >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            sx={{ mb: 2.5 }}
                          />

                          <TextField
                            fullWidth
                            label="Company Website"
                            name="companyName"
                            type="text"
                            autoComplete="organization"
                            onFocus={() => trackFieldFocus('companyName')}
                            inputRef={register({ required: 'Company website is required.' })}
                            error={Boolean(errors.companyName)}
                            helperText={errors.companyName?.message}
                            sx={{ mb: 3 }}
                          />

                          <Box sx={{ mb: 2 }}>
                            <Recaptcha
                              sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}
                              render="explicit"
                              verifyCallback={handleCaptcha}
                              onloadCallback={handleCaptchaLoaded}
                            />
                          </Box>

                          <Box
                            component="label"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '0.9375rem',
                              mb: 3,
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={agreedToTerms}
                              onChange={(e) => setAgreedToTerms(e.target.checked)}
                              style={{ marginRight: 10, transform: 'scale(1.15)' }}
                            />
                            <AgreeToTOCLine />
                          </Box>

                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <SecondaryButton variant="ghost" onClick={handleBack} disabled={submitting}>
                              ← Back
                            </SecondaryButton>
                            <PrimaryButton type="submit" fullWidth disabled={submitting}>
                              {submitting ? 'Starting your trial…' : 'Start my free trial'}
                            </PrimaryButton>
                          </Box>
                          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
                            7-day free trial. Cancel anytime.
                          </Typography>
                        </Box>
                      </Box>
                    </form>
                  )}
                </Card>
              </Grid>

              {/* Value-prop panel (md+ only) */}
              <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 520,
                    borderRadius: 3.5,
                    p: 5,
                    background: (theme) => theme.palette.v2.gradients.signupPanel,
                    color: '#FFFFFF',
                    boxShadow: (theme) => theme.palette.v2.shadows.lg,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      AskNeo • free trial
                    </Typography>
                    <Typography variant="h3" component="div" sx={{ color: '#FFFFFF', mt: 1.5, mb: 3 }}>
                      Real conversations, real conversions.
                    </Typography>
                    <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                      {[
                        'Two-way texting & calling in one shared inbox',
                        'Customizable chatbot & broadcast SMS',
                        'Built-in CRM with tags & assignments'
                      ].map((line) => (
                        <Box
                          key={line}
                          component="li"
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, fontSize: '1rem' }}
                        >
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.16)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              mt: '2px'
                            }}
                          >
                            ✓
                          </Box>
                          <span>{line}</span>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 5,
                      pt: 4,
                      borderTop: '1px solid rgba(255,255,255,0.14)'
                    }}
                  >
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.92)', fontStyle: 'italic', mb: 2 }}>
                      “AskNeo changed how we communicate with clients. We've seen a massive increase in response rates and closed deals.”
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                      Karly M.
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Sales Director
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </ThemeProviderV2>
  );
};

export default SignupPage;
